import React from 'react';

export const VideoThumbnail: React.FC<{
  videoId: string;
  videoUrl: string;
  isAdded?: boolean;
  onAddOrRemoveTimeline: (duration: number, thumbnail?: string) => void;
}> = ({ videoId, videoUrl, isAdded, onAddOrRemoveTimeline }) => {
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [blobUrl, setBlobUrl] = React.useState<string | null>(null);
  const [duration, setDuration] = React.useState<number | null>(null);

  const handleLoadedMetadata = () => {
    const video = videoRef.current;
    if (!video) return;

    const d = video.duration;
    if (Number.isFinite(d) && d > 0) {
      setDuration(Math.floor(d));
      video.currentTime = Math.min(1, d * 0.25);
    }
  };

  const generateThumbnail = () => {
    const video = videoRef.current;
    if (!video || !duration) return;

    const capture = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        const context = canvas.getContext('2d');
        if (!context) return;

        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        const image = canvas.toDataURL('image/png');

        onAddOrRemoveTimeline(duration ?? 0, image);
      } catch (err) {
        console.error('Thumbnail capture failed:', err);
        onAddOrRemoveTimeline(duration ?? 0);
      }
    };

    const targetTime = Math.min(1, duration * 0.25);
    video.addEventListener('seeked', capture, { once: true });
    video.currentTime = targetTime;
  };

  React.useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;

    setBlobUrl(null);
    setDuration(null);

    const fetchBlob = async () => {
      try {
        const res = await fetch(videoUrl);
        if (!res.ok) throw new Error(`Failed to fetch video: ${res.status}`);

        const blob = await res.blob();
        if (cancelled) return;

        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
        setIsLoading(false);
      } catch (err) {
        console.error(
          'VideoThumbnail: blob fetch failed, falling back to direct URL',
          err
        );

        if (!cancelled) setBlobUrl(videoUrl);
        setIsLoading(false);
      }
    };

    fetchBlob();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [videoUrl]);

  return (
    <>
      {blobUrl && (
        <video
          key={videoId}
          muted
          ref={videoRef}
          src={blobUrl}
          controls={false}
          preload="auto"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onLoadedMetadata={handleLoadedMetadata}
          crossOrigin="anonymous"
        />
      )}

      {isLoading && (
        <div
          className={
            'd-flex align-items-center justify-content-center position-absolute w-100 h-100'
          }
          style={{
            position: 'absolute'
          }}>
          <span className={'loader'} role="status" />
        </div>
      )}

      <div
        className={
          'd-flex align-items-center justify-content-center position-absolute'
        }
        style={{
          bottom: '8px',
          right: '8px',
          fontSize: '12px',
          borderRadius: '4px',
          padding: '4px 8px',
          backgroundColor: '#000000B2',
          color: 'white'
        }}>
        {duration ? `${duration}s` : '...'}
      </div>

      <div
        className={'timeline-item-overlay'}
        title={isAdded ? 'Remove from timeline' : 'Add to timeline'}
        onClick={generateThumbnail}
      />
    </>
  );
};
